import { neighbors } from 'topojson-client';
import { scoreToColor } from './areaData.js';

// Area scoring for both kartvy and listvy.
// Each metric is scored by its distance from the typical area around it: a robust z-score
// (median / MAD, on a log scale for skewed metrics) against every area in the same kommun and in the
// kommuns bordering it, mapped to score = 3.5 + z. Most areas land mid-scale and only clear outliers reach the ends.

const SCORE_CENTER = 3.5;
// Score points per robust standard deviation; higher spreads areas further from the middle.
const SCORE_SPREAD = 1;
const MAD_TO_STD = 1.4826;
// Keeps -log(unemployment) finite and damps differences between near-zero values.
const UNEMPLOYMENT_LOG_OFFSET = 0.5;
const MISSING_COLOR = '#363636';
// Minimum number of areas to compare against. Only kommuns without bordering kommuns (islands such as
// Gotland) can fall short; they are topped up with the nearest areas.
const MIN_NEIGHBOURHOOD_SIZE = 20;
// Fewer known values than this for a metric in the neighbourhood falls back to the national median.
const MIN_NEIGHBOURHOOD_VALUES = 5;
// Floor on the local spread, as a share of the national spread, so tiny
// differences in very uniform neighbourhoods don't jump straight to the ends of the scale.
const MIN_NEIGHBOURHOOD_SPREAD = 0.5;

const median = (sortedValues) => sortedValues[sortedValues.length >> 1];

const mean = (values) => {
    const finite = values.filter((value) => Number.isFinite(value));
    return finite.length ? finite.reduce((acc, value) => acc + value, 0) / finite.length : null;
};

// Median, spread and z(value) for the given transformed values.
const buildRobustScale = (values) => {
    const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    if (!sorted.length) return { center: null, scale: 1, toZ: () => null };
    const center = median(sorted);
    const deviations = sorted.map((value) => Math.abs(value - center)).sort((a, b) => a - b);
    const scale = median(deviations) * MAD_TO_STD || 1;
    return {
        center,
        scale,
        toZ: (value) => (Number.isFinite(value) ? (value - center) / scale : null),
    };
};

const zToScore = (z) => {
    if (!Number.isFinite(z)) return null;
    const score = Math.round((SCORE_CENTER + z * SCORE_SPREAD) * 2) / 2;
    return Math.min(6, Math.max(1, score));
};

const METRICS = {
    income: {
        transform: (area) => (area.netInc > 0 ? Math.log(area.netInc) : null),
    },
    owned: {
        transform: (area) => {
            const total = area.rentAp + area.ownedAp + area.house;
            return total > 0 ? (area.ownedAp + area.house) / total : null;
        },
    },
    education: {
        transform: (area) => (Number.isFinite(area.higherEduPop) ? area.higherEduPop : null),
    },
    // Negated so that lower unemployment gives a higher z, like every other metric.
    unemployment: {
        transform: (area, unemploymentScale) => {
            const value = Number(area.unemployment) * unemploymentScale;
            return Number.isFinite(value) && value >= 0 ? -Math.log(value + UNEMPLOYMENT_LOG_OFFSET) : null;
        },
    },
    apartmentPrice: {
        transform: (area) => (area.apartmentAvgSqmPrice > 0 ? Math.log(area.apartmentAvgSqmPrice) : null),
    },
    housePrice: {
        transform: (area) => (area.houseAvgSqmPrice > 0 ? Math.log(area.houseAvgSqmPrice) : null),
    },
};

const COLOR_PREFIX_BY_METRIC = {
    income: 'netInc_',
    owned: 'apartment_',
    education: 'education_',
    unemployment: 'unemployment_',
    apartmentPrice: 'sqm_price_apartment_',
    housePrice: 'sqm_price_house_',
};

// Transformed value per metric per regso code, see METRICS.
const getTransformedValues = (completeData) => {
    const keys = Object.keys(completeData);
    const unemploymentRawValues = keys
        .map((key) => Number(completeData[key].unemployment))
        .filter((value) => Number.isFinite(value));
    const unemploymentScale = unemploymentRawValues.length && Math.max(...unemploymentRawValues) <= 1 ? 100 : 1;

    const transformed = {};
    Object.entries(METRICS).forEach(([metricKey, metric]) => {
        transformed[metricKey] = {};
        keys.forEach((key) => {
            transformed[metricKey][key] = metric.transform(completeData[key], unemploymentScale);
        });
    });
    return { keys, transformed };
};

const buildScoresFromZ = (keys, zByMetric) => {
    const colorCache = {};
    const stats = {};
    const compositeZ = {};
    keys.forEach((key) => {
        Object.entries(COLOR_PREFIX_BY_METRIC).forEach(([metricKey, prefix]) => {
            const score = zToScore(zByMetric[metricKey][key]);
            colorCache[`${prefix}${key}`] = score == null ? MISSING_COLOR : scoreToColor[score];
        });
        // Total uses unrounded z-values; price (apartment + house) counts as one metric.
        compositeZ[key] = mean([
            zByMetric.income[key],
            zByMetric.owned[key],
            zByMetric.education[key],
            zByMetric.unemployment[key],
            mean([zByMetric.apartmentPrice[key], zByMetric.housePrice[key]]),
        ]);
    });

    // Averaging shrinks the spread, so re-standardise the composite onto the same scale.
    const compositeScale = buildRobustScale(keys.map((key) => compositeZ[key]));
    keys.forEach((key) => {
        const score = zToScore(compositeScale.toZ(compositeZ[key])) ?? SCORE_CENTER;
        stats[`combined_${key}`] = score;
        colorCache[`combined_${key}`] = scoreToColor[score];
    });
    return { colorCache, stats };
};

const getBoundsCenter = (topology, geometry) => {
    // Arc coordinates are only needed roughly, to order neighbours by distance.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const visitArc = (arcIndex) => {
        const arc = topology.arcs[arcIndex < 0 ? ~arcIndex : arcIndex];
        let x = 0;
        let y = 0;
        arc.forEach((point, index) => {
            if (topology.transform) {
                x = index === 0 ? point[0] : x + point[0];
                y = index === 0 ? point[1] : y + point[1];
            } else {
                [x, y] = point;
            }
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        });
    };
    const visitRings = (rings) => rings.forEach((ring) => ring.forEach(visitArc));
    if (geometry.type === 'Polygon') visitRings(geometry.arcs);
    else if (geometry.type === 'MultiPolygon') geometry.arcs.forEach(visitRings);
    if (!Number.isFinite(minX)) return null;
    const center = [(minX + maxX) / 2, (minY + maxY) / 2];
    if (!topology.transform) return center;
    const { scale, translate } = topology.transform;
    return [center[0] * scale[0] + translate[0], center[1] * scale[1] + translate[1]];
};

// The comparison group: every area in the area's kommun and in the kommuns that border it (two kommuns
// border each other when any of their areas share a border). Areas of one kommun share the same group
// (a single array, so its scale is computed once). If the group is still smaller than
// MIN_NEIGHBOURHOOD_SIZE, the nearest areas are added ring by ring of shared borders instead.
const buildNeighbourhoods = (topology, keySet) => {
    const objectKey = Object.keys(topology.objects)[0];
    const geometries = topology.objects[objectKey].geometries;
    const codes = geometries.map((geometry) => String(geometry.properties?.regsokod || '').trim());
    const adjacency = neighbors(geometries);
    const kommunOf = (code) => code.slice(0, 4);

    const indexesByKommun = new Map();
    const borderingKommuner = new Map();
    codes.forEach((code, index) => {
        const kommun = kommunOf(code);
        if (!borderingKommuner.has(kommun)) borderingKommuner.set(kommun, new Set());
        adjacency[index].forEach((neighbour) => {
            const other = kommunOf(codes[neighbour]);
            if (other !== kommun) borderingKommuner.get(kommun).add(other);
        });
        if (!keySet.has(code)) return;
        if (!indexesByKommun.has(kommun)) indexesByKommun.set(kommun, []);
        indexesByKommun.get(kommun).push(index);
    });

    const groupByKommun = new Map();
    indexesByKommun.forEach((indexes, kommun) => {
        const group = [...indexes];
        (borderingKommuner.get(kommun) || new Set()).forEach((other) => {
            group.push(...(indexesByKommun.get(other) || []));
        });
        groupByKommun.set(kommun, group.map((member) => codes[member]));
    });

    const centers = new Map();
    const centerOf = (index) => {
        if (!centers.has(index)) centers.set(index, getBoundsCenter(topology, geometries[index]));
        return centers.get(index);
    };
    const distanceSq = (a, b) => {
        const ca = centerOf(a);
        const cb = centerOf(b);
        if (!ca || !cb) return Infinity;
        // Longitude degrees are ~half as long as latitude degrees in Sweden.
        const dx = (ca[0] - cb[0]) * 0.5;
        const dy = ca[1] - cb[1];
        return dx * dx + dy * dy;
    };

    const neighbourhoods = new Map();
    codes.forEach((code, index) => {
        if (!keySet.has(code)) return;
        const group = groupByKommun.get(kommunOf(code)) || [];
        if (group.length > MIN_NEIGHBOURHOOD_SIZE) {
            neighbourhoods.set(code, group);
            return;
        }
        const members = group.filter((member) => member !== code);
        const memberSet = new Set(members);
        const visited = new Set([index]);
        let ring = [index];
        while (members.length < MIN_NEIGHBOURHOOD_SIZE && ring.length) {
            const nextRing = [];
            ring.forEach((ringIndex) => {
                adjacency[ringIndex].forEach((neighbour) => {
                    if (visited.has(neighbour)) return;
                    visited.add(neighbour);
                    nextRing.push(neighbour);
                });
            });
            const candidates = nextRing
                .filter((neighbour) => !memberSet.has(codes[neighbour]) && keySet.has(codes[neighbour]))
                .sort((a, b) => distanceSq(index, a) - distanceSq(index, b));
            for (const candidate of candidates) {
                if (members.length >= MIN_NEIGHBOURHOOD_SIZE) break;
                members.push(codes[candidate]);
                memberSet.add(codes[candidate]);
            }
            ring = nextRing;
        }
        neighbourhoods.set(code, members);
    });
    return neighbourhoods;
};

export function computeNeighbourhoodScoring(completeData, topology) {
    if (completeData == null || topology?.objects == null) return null;
    const { keys, transformed } = getTransformedValues(completeData);
    const neighbourhoods = buildNeighbourhoods(topology, new Set(keys));

    const zByMetric = {};
    Object.keys(METRICS).forEach((metricKey) => {
        const values = transformed[metricKey];
        const national = buildRobustScale(keys.map((key) => values[key]));
        const minScale = national.scale * MIN_NEIGHBOURHOOD_SPREAD;
        zByMetric[metricKey] = {};
        // Areas of one kommun share their group array: its scale is computed once per metric.
        const scaleByGroup = new Map();
        keys.forEach((key) => {
            const value = values[key];
            if (!Number.isFinite(value)) {
                zByMetric[metricKey][key] = null;
                return;
            }
            const group = neighbourhoods.get(key) || [];
            if (!scaleByGroup.has(group)) {
                const groupValues = group
                    .map((neighbour) => values[neighbour])
                    .filter((neighbourValue) => Number.isFinite(neighbourValue));
                scaleByGroup.set(group, groupValues.length < MIN_NEIGHBOURHOOD_VALUES ? null : buildRobustScale(groupValues));
            }
            const local = scaleByGroup.get(group);
            zByMetric[metricKey][key] = local
                ? (value - local.center) / Math.max(local.scale, minScale)
                : national.toZ(value);
        });
    });

    // The groups go along, so a page can say what each area was compared with (neighbourhoodKommuner).
    return { ...buildScoresFromZ(keys, zByMetric), neighbourhoods };
}

// The kommun codes an area's score is weighed against, its own first: those of its comparison group.
export const neighbourhoodKommuner = (neighbourhoods, code) => {
    const kommuner = new Set([String(code).slice(0, 4)]);
    (neighbourhoods?.get(code) || []).forEach((member) => kommuner.add(member.slice(0, 4)));
    return [...kommuner];
};
